import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import {
  Plus,
  Edit,
  Shield,
  Calendar,
  ShieldCheck,
  Users,
  Lock,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  get_designation_list,
  server_post_data,
} from "../ServiceConnection/serviceconnection";

export default function RolesPermissions() {
  const navigate = useNavigate();
  const { toast } = useToast();

  // State management
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch designations
  const fetchDesignations = useCallback(async () => {
    setShowLoaderAdmin(true);
    setLoading(true);
    const fd = new FormData();
    fd.append("flag", "3");

    try {
      const response = await server_post_data(get_designation_list, fd);

      if (!response.data.error) {
        setDesignations(response.data.message.designation || []);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to load designations",
          variant: "destructive",
        });
        setDesignations([]);
      }
    } catch (error) {
      console.error("Error fetching designations:", error);
      toast({
        title: "Error",
        description: "Failed to load designations",
        variant: "destructive",
      });
      setDesignations([]);
    } finally {
      setShowLoaderAdmin(false);
      setLoading(false);
    }
  }, [toast]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Initial load
  useEffect(() => {
    fetchDesignations();
  }, [fetchDesignations]);

  return (
    <MainLayout>
      <Header
        title="Roles & Permissions"
        subtitle="Manage staff designations and access levels"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {designations.length}
                </p>
                <p className="text-sm text-muted-foreground">Total Roles</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {
                    designations.filter((d) => d.designation_name !== "Owner")
                      .length
                  }
                </p>
                <p className="text-sm text-muted-foreground">Active Roles</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {
                    designations.filter((d) => d.designation_name === "Owner")
                      .length
                  }
                </p>
                <p className="text-sm text-muted-foreground">System Roles</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button
            onClick={() => navigate("/view-permissions")}
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
          >
            <Lock className="w-4 h-4" />
            View Permissions
          </Button>
          <Button
            onClick={() => navigate("/add-roles")}
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            Add Role
          </Button>
        </div>

        {/* Designations Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          {loading ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <p>Loading Roles...</p>
              </CardContent>
            </Card>
          ) : designations.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No roles found</p>
              </CardContent>
            </Card>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground w-[200px]">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Created Date
                    </div>
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Designation Name
                    </div>
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Status
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {designations.map((designation, index) => (
                  <TableRow
                    key={designation.primary_id}
                    className="border-border hover:bg-muted/30"
                  >
                    <TableCell className="text-muted-foreground">
                      {formatDate(designation.entry_date)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center">
                          <Shield className="h-5 w-5 text-primary-foreground" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">
                            {designation.designation_name}
                          </p>
                          {designation.designation_name === "Owner" && (
                            <p className="text-xs text-muted-foreground">
                              System Role
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {designation.designation_name === "Owner" ? (
                        <Badge className="bg-primary/20 text-primary border-primary/30">
                          System Role
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="border-success/30 text-success"
                        >
                          Active
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {designation.designation_name !== "Owner" && (
                        <Link to={`/edit-roles/${designation.primary_id}`}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="hover:bg-primary/10 hover:text-primary"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </Link>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
